import React, { useMemo } from "react";
import { useFirestoreCollection } from "../../../lib/useFirestore.js";
import { Crumbs } from "./nav.jsx";

/**
 * Screen 1 — "Which job?" Jobs that have captures, newest first. Each row's chip
 * is derived from the job's sheet state (slice-1 denormalized hint on the job
 * doc; a later slice computes it from the captureSheet subcollection + files):
 *   in_files     → "sheet in job files"  (green)  — a contact sheet already shipped
 *   thumbs_ready → "thumbnails ready"     (blue)   — bridge uploaded thumbs, not sent
 *   (default)    → "no sheet yet"         (gray)
 */
const monthDay = (iso) => {
  if (!iso) return "";
  const [, m, d] = iso.split("-").map(Number);
  const MON = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  return `${MON[(m || 1) - 1]} ${d}`;
};

function jobChip(job) {
  if (job.sheetState === "in_files") return { cls: "green", text: "sheet in job files" };
  // C3: a built-but-unpushed PDF exists — a weaker "in_files".
  if (job.sheetState === "pdf_built") return { cls: "green", text: "PDF built" };
  if (job.sheetState === "thumbs_ready") return { cls: "blue", text: "thumbnails ready" };
  if (job.sheetState === "thumbs_partial") {
    // F8: report readiness honestly — some captures still uploading.
    const ready = job.thumbsReadyCount ?? 0;
    const total = job.captureCount ?? 0;
    return { cls: "amber", text: `thumbnails ${ready}/${total}` };
  }
  return { cls: "gray", text: "no sheet yet" };
}

export default function SheetsHome({ go }) {
  const { data: jobs, loading } = useFirestoreCollection("jobs");

  const list = useMemo(() => {
    return jobs
      .filter((j) => !j.deletedAt && (j.captureCount > 0 || j.sheetState))
      .sort((a, b) => (b.shootDates?.[0] || "").localeCompare(a.shootDates?.[0] || ""));
  }, [jobs]);

  return (
    <>
      <Crumbs trail={[{ label: "Sheets" }]} go={go} />
      <div className="qhead">Which job?<span className="d">Jobs with captures, newest first</span></div>

      {loading ? (
        <div className="empty">Loading jobs…</div>
      ) : list.length === 0 ? (
        <div className="empty">No jobs with captures yet. Seed one with <span className="mono">tools/devSeedSheets.mjs</span>.</div>
      ) : (
        list.map((j) => {
          const chip = jobChip(j);
          const dates = j.shootDates || [];
          const multi = dates.length > 1;
          const label = j.displayName || j.clientName || j.jobId || j.id;
          const sub = [monthDay(dates[0]), j.captureCount ? `${j.captureCount} captures` : null, multi ? "multi-day" : null]
            .filter(Boolean).join(" · ");
          return (
            <button
              key={j.id}
              type="button"
              className="row"
              onClick={() => go("job", { jobId: j.id, jobLabel: label, groupId: null })}
            >
              <span className="num mono">{j.jobId || j.id}</span>
              <span className="grow">{j.clientName || label}<br /><span className="sub2">{sub}</span></span>
              <span className={`chip ${chip.cls}`}>{chip.text}</span>
            </button>
          );
        })
      )}
    </>
  );
}
